import * as React from 'react';
import { compareAsc, format } from 'date-fns';
import { Calendar as CalendarIcon, CircleXIcon, CalendarDays } from 'lucide-react';
import {
    Popover,
    PopoverContent,
    PopoverTrigger
} from '../common/Popover';
import { isDateRange } from 'react-day-picker';
import { ReactDayPickerProps, ReactDayPicker } from './ReactDayPicker';
import { useTranslation } from 'react-i18next';
import { getLocaleDateFns } from '@shared/utils/language';
import { Button } from '../common/Button';
import { cn } from '@shared/utils/utils';
import classNames from 'classnames';
import { ja } from 'date-fns/locale';
import { DATE_YMD_FNS } from '../constants/common.constants';

type CalendarProps = ReactDayPickerProps & {
    mode: 'single' | 'multiple' | 'range',
    selected: any,
    onSelect: any,
    type?: 'calendar' | 'datePicker',
    placeholder?: string,
    useIconRemove?: boolean,
    classButton?: string,
    disableButton?: boolean
};

export default function Calendar({
    type = 'datePicker',
    mode,
    selected,
    onSelect,
    onDayClick,
    placeholder = 'Select date',
    useIconRemove = true,
    classButton = '',
    disableButton = false,
    ...props
}: CalendarProps) {
    // hooks
    const { i18n: { language } } = useTranslation();
    const localeDateFns = getLocaleDateFns(language);

    // state
    const [openCalendar, setOpenCalendar] = React.useState<boolean>(false);
    const [countClickDays, setCountClickDays] = React.useState<number>(0);

    // function
    const onCheckCloseCalendarRange = (date: Date) => {
        if (countClickDays > 0 && selected?.from && compareAsc(selected.from, date) !== 0) {
            setOpenCalendar(false);
            setCountClickDays(0);
        } else {
            setCountClickDays(state => state + 1);
        }
    };

    // render
    const CalendarChildren = () => {
        const defaultMonth = mode === 'single' ?
            selected :
            (
                mode === 'range' ?
                    (selected?.from ?? selected?.to) :
                    (
                        mode === 'multiple' ? selected?.[selected?.length - 1] : undefined
                    )
            );

        return (
            <ReactDayPicker
                mode={mode}
                locale={ja}
                selected={selected}
                onSelect={onSelect}
                onDayClick={(date, modifiers, e) => {
                    onDayClick && onDayClick(date, modifiers, e);
                    mode === 'single' && setOpenCalendar(false);
                    mode === 'range' && onCheckCloseCalendarRange(date);
                }}
                defaultMonth={defaultMonth}
                {...props}
            />
        );
    };

    if (type === 'calendar') {
        return <CalendarChildren />;
    }

    return (
        <Popover open={openCalendar} onOpenChange={setOpenCalendar}>
            <PopoverTrigger asChild>
                <Button
                    variant='outline'
                    className={cn(
                        'relative justify-between text-left font-normal',
                        'min-w-[280px]',
                        !selected && 'text-muted-foreground',
                        selected && useIconRemove && 'pr-9',
                        classButton
                    )}
                    disabled={disableButton}
                >
                    {
                        selected && selected instanceof Date && format(selected, DATE_YMD_FNS)
                    }
                    {
                        (selected instanceof Array && selected.length > 0) && (
                            selected.length > 3 ?
                                `${selected.length} selected date` :
                                selected.map(selectDate => format(selectDate, DATE_YMD_FNS)).join(', '))
                    }
                    {
                        selected && isDateRange(selected) && selected.from &&
                        `${format(selected.from, DATE_YMD_FNS)} ~ ${selected.to ? format(selected.to, DATE_YMD_FNS) : ''}`
                    }
                    {
                        (!selected || (selected instanceof Array && selected.length === 0)) &&
                        <span>{placeholder}</span>
                    }
                    {
                        selected && useIconRemove && (
                            <CircleXIcon
                                className='h-5 w-5 absolute right-4 text-[#848484] hover:text-red-500'
                                onPointerDown={() => {
                                    onSelect && onSelect(undefined);
                                    mode === 'range' && setCountClickDays(0);
                                }}
                            />
                        )
                    }
                    <CalendarDays color='#848484' className={classNames('h-5 w-5', useIconRemove && 'mr-2')} />
                </Button>
            </PopoverTrigger>
            <PopoverContent className='w-auto p-0'>
                <CalendarChildren />
            </PopoverContent>
        </Popover>
    );
}
